<script setup lang="ts">
import { computedAsync, useElementSize } from '@vueuse/core';
import { computed, ref } from 'vue';

import { PriceGuaranteeBanner } from '@apps/main/new-desktop/modules/priceGuarantee';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppButton, AppLinkButton, AppFlicker, AppSvgIcon } from '@libs/ui';
import { getDomain } from '@libs/utils';
import { isExperimentAsync } from '@libs/feature-toggle';

import { useGlobalCurrencyStore } from '../../../../core/stores/globalCurrencyStore';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripSummaryStore,
} from '../../stores';

import InstalmentBanner from './InstalmentBanner.vue';

interface TripSummaryColumnFooterProps {
  totalPriceWithAddons: number;
  paxes: string;
  travelDates: string;
  shouldShowShareButton: boolean;
}

const props = defineProps<TripSummaryColumnFooterProps>();

interface TripSummaryColumnFooterEmits {
  (eventName: 'total-price-clicked'): void;
}

const emits = defineEmits<TripSummaryColumnFooterEmits>();

const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripSummaryStore = useTripSummaryStore();
const globalCurrencyStore = useGlobalCurrencyStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const tripSummaryColumnFooterRef = ref();
const { width } = useElementSize(tripSummaryColumnFooterRef);
const bookButtonSizeThreshold = 480;

const { t } = i18n.global;
const LOCALE_ALLOWED_TO_DISPLAY_INSTALMENT_BANNER = ['sv', 'no'];

const bookButtonText = computed(() =>
  browserStore.isSmallDevice
    ? t('planner.book_trip')
    : t('planner.booking_proceed')
);

const isSummaryButtonExperiment = computedAsync<boolean>(
  () => isExperimentAsync('summary_button_experiment'),
  false
);

const canShowInstalmentBanner = computed(() =>
  LOCALE_ALLOWED_TO_DISPLAY_INSTALMENT_BANNER.includes(
    getDomain.getCurrentDomainSpec().Locale
  )
);

const bookButtonSize = computed(() =>
  width.value < bookButtonSizeThreshold ? 'medium' : 'large'
);

function openTripSummary(): void {
  if (browserStore.isLargerThanMediumMax) {
    statsigEventsStore.logTripOverviewTripSummaryPanelOpened();
    emits('total-price-clicked');
  }
}
</script>

<template>
  <div ref="tripSummaryColumnFooterRef" class="trip-summary-column-footer">
    <div class="banners">
      <PriceGuaranteeBanner
        @learn-more="tripOverviewModalsStore.openPriceGuaranteeModal"
      />

      <div v-if="canShowInstalmentBanner" class="separator" />
      <InstalmentBanner v-if="canShowInstalmentBanner" />
    </div>

    <div v-if="isSummaryButtonExperiment" class="trip-details">
      <div class="trip-general-info">
        <div class="section">
          <AppSvgIcon class="icon" name="people" />
          {{ props.paxes }}
        </div>
        <div v-if="props.travelDates.length" class="section">
          <AppSvgIcon name="calendarOutlined" />
          {{ props.travelDates }}
        </div>
        <AppFlicker v-else width="spacing-96" height="spacing-20" />
      </div>

      <div class="trip-summary">
        <button
          v-if="tripOverviewPageStore.anyItineraryLoaded"
          class="total-price-button"
        >
          {{ globalCurrencyStore.formatMoney(props.totalPriceWithAddons) }}
        </button>
        <AppFlicker
          v-else
          width="spacing-96"
          height="spacing-40"
          class="total-price-button"
        />
        <AppLinkButton
          class="trip-summary-link-button"
          :text="$t('planner.show_trip_summary')"
          size="large"
          @click="openTripSummary"
        />
      </div>
    </div>

    <template v-else>
      <div class="trip-info">
        <div v-if="props.travelDates.length" class="date">
          {{ props.travelDates }}
        </div>
        <AppFlicker v-else width="spacing-96" height="spacing-20" />

        <div class="pax">
          {{ props.paxes }}
        </div>
      </div>

      <button
        v-if="tripOverviewPageStore.anyItineraryLoaded"
        class="total-price"
        @click="openTripSummary"
      >
        {{ globalCurrencyStore.formatMoney(props.totalPriceWithAddons) }}
      </button>
      <AppFlicker
        v-else
        width="spacing-96"
        height="spacing-40"
        class="total-price"
      />
    </template>

    <div class="trip-summary-actions">
      <AppButton
        type="important"
        :size="bookButtonSize"
        :wide="true"
        :state="tripSummaryStore.bookingButtonState"
        @click="tripSummaryStore.onBookClicked"
      >
        {{ bookButtonText }}
      </AppButton>

      <AppButton
        v-if="props.shouldShowShareButton"
        type="text"
        state="enabled"
        size="medium"
        :wide="true"
        @click="tripSummaryStore.openShareWidget"
      >
        {{ $t('planner.share_or_save_trip') }}
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.trip-summary-column-footer {
  display: flex;
  flex-direction: column;

  .banners {
    margin-bottom: var(--spacing-24);

    .separator {
      margin: var(--spacing-4) 0;
      border-top: 1px solid var(--color-secondary-90);
    }
  }

  .trip-details {
    margin-bottom: var(--spacing-16);
    display: flex;
    gap: var(--spacing-8);

    .trip-general-info {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: var(--spacing-12);

      .section {
        display: flex;
        align-items: center;
        gap: var(--spacing-8);
        color: var(--color-secondary-40);
        @include font-label-l-light;

        .icon {
          color: var(--color-black);
        }
      }
    }

    .trip-summary {
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: right;

      .total-price-button {
        all: unset;
        @include font-heading-2;
        color: var(--color-secondary-40);
      }

      .trip-summary-link-button {
        color: var(--color-secondary-40);
      }
    }
  }

  .trip-info {
    display: flex;
    margin-bottom: var(--spacing-16);
    align-self: center;

    .date {
      color: var(--color-secondary-60);
      @include font-label-l-light;
    }

    .pax {
      margin-left: var(--spacing-16);
      color: var(--color-secondary-60);
      @include font-label-l-light;
    }
  }

  .total-price {
    all: unset;
    @include font-heading-2;
    color: var(--color-secondary-40);
    margin-bottom: var(--spacing-24);
    align-self: center;

    @include on-hover {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .trip-summary-actions {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);
  }
}
</style>
